/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import {
  FEATURE_TIERS,
  getTierFeatures,
  type FeatureTier,
} from "@/lib/constants/features/flags";
import type { SubscriptionPlan } from "@/lib/constants/subscriptions";

export interface FeatureAccessResult {
  allowed: boolean;
  reason?: string;
  requiredTier?: string;
}

/**
 * Maps subscription plans to feature tiers
 * - trial_org gets Starter features (7-day trial)
 * - mobile_plan gets Mobile features (field-focused)
 * - starter_plan gets Starter features (full platform)
 * - professional_plan gets Professional features (team + integrations)
 * - business_plan gets Business features (API + advanced)
 */
const PLAN_TO_TIER: Record<SubscriptionPlan, FeatureTier> = {
  trial_org: FEATURE_TIERS.STARTER,
  mobile_plan: FEATURE_TIERS.MOBILE,
  starter_plan: FEATURE_TIERS.STARTER,
  professional_plan: FEATURE_TIERS.PROFESSIONAL,
  business_plan: FEATURE_TIERS.BUSINESS,
};

export function validateFeatureAccess(
  featureFlag: string,
  subscriptionPlan: SubscriptionPlan,
  enforceTiers: boolean = true,
): FeatureAccessResult {
  if (!enforceTiers) {
    return { allowed: true };
  }

  const tier = PLAN_TO_TIER[subscriptionPlan];
  if (!tier) {
    // Unknown plan - default to most restrictive (Mobile tier)
    return {
      allowed: getTierFeatures(FEATURE_TIERS.MOBILE).includes(featureFlag),
      reason: tier ? undefined : "Unknown subscription plan",
    };
  }

  const availableFeatures = getTierFeatures(tier);
  if (!availableFeatures.includes(featureFlag)) {
    // Find the minimum tier that includes this feature
    const TIER_ORDER: FeatureTier[] = [
      FEATURE_TIERS.MOBILE,
      FEATURE_TIERS.STARTER,
      FEATURE_TIERS.PROFESSIONAL,
      FEATURE_TIERS.BUSINESS,
      FEATURE_TIERS.ENTERPRISE,
    ];
    const requiredTier = TIER_ORDER.find((t) =>
      getTierFeatures(t).includes(featureFlag),
    );

    return {
      allowed: false,
      reason: `Feature "${featureFlag}" requires a higher tier plan`,
      requiredTier: requiredTier ?? "professional",
    };
  }

  return { allowed: true };
}
